<script setup lang="ts">
import { computed } from 'vue'
import { default as PButton } from 'primevue/button'
import CBaseBlock from '@/presentation/components/base/CBaseBlock.vue'
import type { LearningModuleInput } from '@/core/domain/learning/module/LearningModule.ts'
import type { LearningGrain } from '@/core/domain/learning/grain/LearningGrain.ts'

const currentDisplayedGrain = defineModel<LearningGrain | null>('currentDisplayedGrain', {
  required: true
})
const fieldToUpdate = defineModel<string | null>('fieldToUpdate', {
  required: true
})

const props = defineProps<{
  isCreatingModule: boolean
  learningModule: LearningModuleInput
  hasUnsavedGrains: boolean
}>()

const canAddGrain = computed(() => {
  return !props.isCreatingModule && !props.hasUnsavedGrains
})

const addGrainTooltip = computed(() => {
  if (props.isCreatingModule) {
    return "Veuillez enregistrer le module avant d'ajouter un grain"
  }
  if (props.hasUnsavedGrains) {
    return "Veuillez valider ou annuler les grains non sauvegardés avant d'ajouter un nouveau grain"
  }
  return ''
})

const displayGrain = (grain: LearningGrain) => {
  if (currentDisplayedGrain.value) currentDisplayedGrain.value.deleteAllSupportDrafts()
  currentDisplayedGrain.value = grain
}

function addGrainToModule() {
  const newGrain = props.learningModule.addNewGrain()
  currentDisplayedGrain.value = newGrain
  fieldToUpdate.value = `grain_${newGrain.order}`
}
</script>

<template>
  <c-base-block text>
    <template #title>
      <h5 class="font-bold">Grains</h5>
    </template>

    <div
      v-for="(grain, index) in learningModule.sortedLearningGrains"
      :key="`grain_${index}`"
      class="mt-4"
      data-testid="grain-item"
      :class="{
        'text-primary': currentDisplayedGrain && currentDisplayedGrain.order === grain.order,
        'cursor-pointer': !currentDisplayedGrain || currentDisplayedGrain.order !== grain.order,
        'font-bold': currentDisplayedGrain && currentDisplayedGrain.order === grain.order
      }"
      @click="displayGrain(grain)"
    >
      <div class="flex items-center gap-2">
        <i
          class="pi"
          :class="[
            grain.isActive ? 'pi-check-circle text-emerald-500' : 'pi-file-edit text-gray-500'
          ]"
        />
        <span>{{ grain.title }}</span>

        <!-- Compteur de supports -->
        <span class="ml-auto text-xs text-gray-500">
          <i class="pi pi-file" />
          {{ grain.supportsCount }}
        </span>

        <!-- Indicateur quiz invalide -->
        <i
          v-if="!grain.canBePublished && grain.isDraft"
          class="pi pi-exclamation-triangle text-orange-500"
          v-tooltip="grain.getQuizValidationMessages().join(', ')"
        />
      </div>
    </div>

    <p-button
      label="Ajouter un grain"
      icon="pi pi-plus"
      class="pointer-events-auto mt-4"
      @click="addGrainToModule"
      outlined
      size="small"
      :disabled="!canAddGrain"
      v-tooltip="addGrainTooltip"
    />
  </c-base-block>
</template>
